import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authMethodsApi } from '../services/authMethodsApi';
import type { AuthMethod, AuthMethodConfig } from '../types/authMethods';

// Query keys
export const authMethodsKeys = {
  all: ['auth-methods'] as const,
  lists: () => [...authMethodsKeys.all, 'list'] as const,
  list: (projectId: string) => [...authMethodsKeys.lists(), projectId] as const,
};

// ============ Query Hooks ============

export function useAuthMethods(projectId: string | undefined) {
  return useQuery({
    queryKey: authMethodsKeys.list(projectId!),
    queryFn: async () => {
      const response = await authMethodsApi.list(projectId!);
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch auth methods');
      }
      return response.data!;
    },
    enabled: !!projectId,
  });
}

// ============ Mutation Hooks ============

export function useUpdateAuthMethods() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      authMethods,
    }: {
      projectId: string;
      authMethods: AuthMethod[];
    }) => {
      const response = await authMethodsApi.update(projectId, authMethods);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update auth methods');
      }
      return response.data!;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: authMethodsKeys.list(variables.projectId) });
      // Also invalidate project queries since enabled_auth_methods is included there
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
    },
  });
}

export function useEnableAuthMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      method,
    }: {
      projectId: string;
      method: AuthMethod;
    }) => {
      const response = await authMethodsApi.enable(projectId, method);
      if (!response.success) {
        throw new Error(response.error || 'Failed to enable auth method');
      }
      return response.data!;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: authMethodsKeys.list(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
    },
  });
}

export function useDisableAuthMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      method,
    }: {
      projectId: string;
      method: AuthMethod;
    }) => {
      const response = await authMethodsApi.disable(projectId, method);
      if (!response.success) {
        throw new Error(response.error || 'Failed to disable auth method');
      }
      return response.data!;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: authMethodsKeys.list(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
    },
  });
}

// ============ Helper Types ============

export interface AuthMethodsData {
  auth_methods: AuthMethodConfig[];
  available_methods: AuthMethod[];
  oauth_providers: AuthMethod[];
}
